import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import type { DemoAttendeeRow } from "../sql";
import { SIGNUP_TIMING_LABELS, SIGNUP_TIMING_ORDER } from "../helpers";
import type { ViewMode } from "../helpers";
import { PRIMARY, MUTED, FG1 } from "../../../lib/funnel/theme";
import { fmtN, pctVal } from "../../../lib/funnel/helpers";
import { InfoButton } from "../../../lib/funnel/InfoButton";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

// Smaller bar chart that breaks the "Signed up" stage out by signup timing
// relative to the demo (before / on day / within 7 / 30 / later / never).
// Lives below the funnel because timing is a separate question from the
// stage-to-stage funnel.

const BUCKET_COLOURS: Record<string, string> = {
  before: "#A5C6CF",
  on_day: "#74A8B5",
  within_7d: PRIMARY,
  within_30d: "#236E80",
  later: "#1B5563",
  never: MUTED,
};

export function SignupTimingChart({ rows, viewMode, pagePills }: { rows: DemoAttendeeRow[]; viewMode: ViewMode; pagePills?: FilterPillSpec[] }) {
  const isCompany = viewMode === "company";
  const unitLong = isCompany ? "prospect companies" : "attendee rows";
  const unitTitle = isCompany ? "Companies" : "Attendees";
  const data = useMemo(() => {
    const total = rows.length;
    return SIGNUP_TIMING_ORDER.map((b) => {
      const count = rows.filter((r) => r.signup_timing_bucket === b).length;
      return {
        bucket: b,
        label: SIGNUP_TIMING_LABELS[b],
        count,
        pct: pctVal(count, total),
      };
    });
  }, [rows]);

  const total = rows.length;

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
            Signup timing relative to demo
            <InfoButton label="Signup timing">
              <p style={{ margin: 0 }}>
                Of {unitLong} in cohort, when did {isCompany ? "the earliest signup-aligned attendee" : "each one"} sign up relative to the demo? <code>before</code> = signed up
                ≥1 day prior; <code>on day</code> = within ±1 day; later buckets are post-demo.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={`${fmtN(total)} ${unitLong}, % of cohort.`}
        filterPills={pagePills}
      />
      <div className="card-body">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} margin={{ top: 24, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.1)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: FG1 }} interval={0} />
            <YAxis
              tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
              tick={{ fontSize: 10, fill: MUTED }}
              domain={[0, 100]}
              width={42}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              formatter={(_v, _name, item) => {
                const row = item?.payload as { count: number; pct: number } | undefined;
                const count = row?.count ?? 0;
                const pct = row?.pct ?? 0;
                return [`${pct.toFixed(1)}% (${fmtN(count)}/${fmtN(total)})`, unitTitle] as [string, string];
              }}
            />
            <Bar dataKey="pct" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.bucket} fill={BUCKET_COLOURS[d.bucket] ?? PRIMARY} />
              ))}
              <LabelList
                dataKey="pct"
                position="top"
                formatter={(v: unknown) => `${Number(v).toFixed(1)}%`}
                style={{ fontSize: 10, fill: FG1, fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
